import { ChangeEvent, FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Eye, ImagePlus, RefreshCw, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminHomeSection,
  AdminHomeSectionPayload,
  AdminUser,
  getCurrentAdmin,
  listAdminHomeSections,
  logout,
  updateAdminHomeSection,
  uploadAdminHomeSectionImage,
} from "./api";

type HomeSectionForm = AdminHomeSectionPayload & {
  TagsText: string;
};

type Notice = {
  tone: "success" | "error" | "info";
  message: string;
};

function toForm(item: AdminHomeSection): HomeSectionForm {
  return {
    AdminTitle: item.AdminTitle || "",
    Eyebrow: item.Eyebrow || "",
    Title: item.Title || "",
    Subtitle: item.Subtitle || "",
    Description: item.Description || "",
    BadgeText: item.BadgeText || "",
    ImageUrl: item.ImageUrl || "",
    MobileImageUrl: item.MobileImageUrl || "",
    ImageAlt: item.ImageAlt || "",
    PrimaryButtonLabel: item.PrimaryButtonLabel || "",
    PrimaryButtonUrl: item.PrimaryButtonUrl || "",
    SecondaryButtonLabel: item.SecondaryButtonLabel || "",
    SecondaryButtonUrl: item.SecondaryButtonUrl || "",
    NoteText: item.NoteText || "",
    Tags: item.Tags || [],
    TagsText: (item.Tags || []).join("\n"),
    IsActive: item.IsActive,
    SortOrder: item.SortOrder,
  };
}

function toPayload(form: HomeSectionForm): AdminHomeSectionPayload {
  return {
    AdminTitle: form.AdminTitle.trim(),
    Eyebrow: form.Eyebrow?.trim() || null,
    Title: form.Title.trim(),
    Subtitle: form.Subtitle?.trim() || null,
    Description: form.Description?.trim() || null,
    BadgeText: form.BadgeText?.trim() || null,
    ImageUrl: form.ImageUrl?.trim() || null,
    MobileImageUrl: form.MobileImageUrl?.trim() || null,
    ImageAlt: form.ImageAlt?.trim() || null,
    PrimaryButtonLabel: form.PrimaryButtonLabel?.trim() || null,
    PrimaryButtonUrl: form.PrimaryButtonUrl?.trim() || null,
    SecondaryButtonLabel: form.SecondaryButtonLabel?.trim() || null,
    SecondaryButtonUrl: form.SecondaryButtonUrl?.trim() || null,
    NoteText: form.NoteText?.trim() || null,
    Tags: form.TagsText.split(/\r?\n|,/).map((tag) => tag.trim()).filter(Boolean),
    IsActive: form.IsActive,
    SortOrder: Number(form.SortOrder || 100),
  };
}

const emptyForm: HomeSectionForm = {
  AdminTitle: "",
  Eyebrow: "",
  Title: "",
  Subtitle: "",
  Description: "",
  BadgeText: "",
  ImageUrl: "",
  MobileImageUrl: "",
  ImageAlt: "",
  PrimaryButtonLabel: "",
  PrimaryButtonUrl: "",
  SecondaryButtonLabel: "",
  SecondaryButtonUrl: "",
  NoteText: "",
  Tags: [],
  TagsText: "",
  IsActive: true,
  SortOrder: 100,
};

function sectionHint(key: string) {
  if (key === "hero") return "首頁第一屏主視覺。";
  if (key === "promo") return "首頁第二塊左右圖文主題，適合活動、新品或服務亮點。";
  if (key === "products") return "首頁商品推薦標題與提示文字；商品清單仍由商品管理的精選商品決定。";
  return "首頁固定區塊內容。";
}

export default function AdminHomeSections() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminHomeSection[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState<HomeSectionForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"ImageUrl" | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const selectedItem = useMemo(() => items.find((item) => item.HomeSectionId === selectedId) || null, [items, selectedId]);
  const selectedSectionKey = selectedItem?.SectionKey || "";
  const showSubtitle = selectedSectionKey !== "products";
  const showImageFields = selectedSectionKey === "hero" || selectedSectionKey === "promo";
  const showSecondaryButton = selectedSectionKey === "hero";
  const showMultilineTitles = selectedSectionKey === "promo";

  async function load(preferredId = selectedId) {
    setLoading(true);
    setNotice(null);
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminHomeSections();
      setItems(data.Items);
      const nextSelected = data.Items.find((item) => item.HomeSectionId === preferredId) || data.Items[0] || null;
      setSelectedId(nextSelected?.HomeSectionId || "");
      setForm(nextSelected ? toForm(nextSelected) : emptyForm);
      setErrors({});
    } catch (error) {
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "首頁區塊讀取失敗" });
      if (error instanceof AdminApiError && error.status === 401) {
        navigate("/admin-login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function choose(item: AdminHomeSection) {
    setSelectedId(item.HomeSectionId);
    setForm(toForm(item));
    setErrors({});
    setNotice(null);
  }

  function updateField<K extends keyof HomeSectionForm>(key: K, value: HomeSectionForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleUpload(event: ChangeEvent<HTMLInputElement>, field: "ImageUrl") {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(field);
    setNotice(null);
    try {
      const url = await uploadAdminHomeSectionImage(file);
      updateField(field, url);
    } catch (error) {
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "圖片上傳失敗" });
    } finally {
      setUploading(null);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedItem) return;
    setSaving(true);
    setErrors({});
    setNotice(null);
    try {
      await updateAdminHomeSection(selectedItem.HomeSectionId, toPayload(form));
      setNotice({ tone: "success", message: "首頁區塊已儲存。" });
      await load(selectedItem.HomeSectionId);
      setNotice({ tone: "success", message: "首頁區塊已儲存。" });
    } catch (error) {
      if (error instanceof AdminApiError) {
        setErrors(error.errors || {});
      }
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "儲存失敗" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="內容管理" title="首頁區塊管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="max-w-3xl text-sm leading-relaxed text-[#59665f]">
          管理首頁上方三個固定主題區塊。版型由前台控制，這裡維護文字、圖片、按鈕連結、排序與上下架。
        </p>
        <div className="flex gap-2">
          <a className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" href="/" target="_blank" rel="noreferrer">
            <Eye size={17} />
            查看首頁
          </a>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load(selectedId)} type="button">
            <RefreshCw size={17} />
            重新整理
          </button>
        </div>
      </div>

      {notice && (
        <div className="mb-4">
          <NoticeMessage notice={notice} />
        </div>
      )}

      <div className="grid min-h-0 gap-4 lg:h-[calc(100vh-164px)] lg:grid-cols-[360px_1fr]">
        <div className="rounded-md border border-[#dfe6e1] bg-white p-3 lg:sticky lg:top-[84px] lg:flex lg:max-h-[calc(100vh-164px)] lg:flex-col">
          <div className="mb-3 text-sm font-bold text-[#315746]">首頁區塊</div>
          <div className="space-y-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
            {loading ? (
              <div className="rounded-md bg-[#f6f7f5] px-4 py-8 text-center text-sm text-[#59665f]">讀取中...</div>
            ) : items.length === 0 ? (
              <div className="rounded-md bg-[#f6f7f5] px-4 py-8 text-center text-sm text-[#59665f]">尚未建立首頁區塊，請先執行資料庫 migration。</div>
            ) : items.map((item) => (
              <button
                className={`w-full rounded-md border px-4 py-3 text-left transition ${item.HomeSectionId === selectedId ? "border-[#315746] bg-[#e9f4ec]" : "border-[#dfe6e1] bg-white hover:bg-[#f6f7f5]"}`}
                key={item.HomeSectionId}
                onClick={() => choose(item)}
                type="button"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[#203d32]">{item.AdminTitle}</p>
                    <p className="mt-1 text-xs text-[#7b877f]">{sectionHint(item.SectionKey)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>
                    {item.IsActive ? "上架" : "下架"}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-[#7b877f]">
                  <code>{item.SectionKey}</code>
                  <span>排序 {item.SortOrder}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <form className="flex min-h-0 flex-col overflow-hidden rounded-md border border-[#dfe6e1] bg-white" onSubmit={handleSubmit}>
          {!selectedItem ? (
            <div className="py-16 text-center text-[#59665f]">請選擇要編輯的首頁區塊。</div>
          ) : (
            <>
              <div className="shrink-0 border-b border-[#edf0ee] bg-white px-4 py-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#203d32]">{selectedItem.AdminTitle}</h2>
                  <p className="mt-1 text-sm text-[#7b877f]">{sectionHint(selectedItem.SectionKey)}</p>
                </div>
                <label className={`inline-flex cursor-pointer items-center gap-3 rounded-md border px-4 py-2.5 text-sm font-bold transition ${
                  form.IsActive ? "border-[#b8d8c0] bg-[#e9f4ec] text-[#245438]" : "border-[#dfe6e1] bg-[#f6f7f5] text-[#59665f]"
                }`}>
                  <input checked={form.IsActive} className="sr-only" onChange={(event) => updateField("IsActive", event.target.checked)} type="checkbox" />
                  <span className={`relative h-6 w-11 rounded-full transition ${form.IsActive ? "bg-[#315746]" : "bg-[#b8c2bc]"}`}>
                    <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${form.IsActive ? "left-6" : "left-1"}`} />
                  </span>
                  <span>{form.IsActive ? "上架顯示中" : "目前下架"}</span>
                </label>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-28">
                <div className="grid gap-4 md:grid-cols-2">
                <Field error={errors.AdminTitle} label="後台區塊名稱">
                  <input className={inputClass} value={form.AdminTitle} onChange={(event) => updateField("AdminTitle", event.target.value)} />
                </Field>
                <Field label="排序">
                  <input className={inputClass} min={1} type="number" value={form.SortOrder} onChange={(event) => updateField("SortOrder", Number(event.target.value))} />
                </Field>
                <Field label="小標 / Eyebrow">
                  <input className={inputClass} value={form.Eyebrow || ""} onChange={(event) => updateField("Eyebrow", event.target.value)} />
                </Field>
                <Field label="徽章文字">
                  <input className={inputClass} value={form.BadgeText || ""} onChange={(event) => updateField("BadgeText", event.target.value)} />
                </Field>
                <Field className="md:col-span-2" error={errors.Title} label="主標題">
                  {showMultilineTitles ? (
                    <textarea className={`${inputClass} min-h-[96px] py-3`} value={form.Title} onChange={(event) => updateField("Title", event.target.value)} />
                  ) : (
                    <input className={inputClass} value={form.Title} onChange={(event) => updateField("Title", event.target.value)} />
                  )}
                </Field>
                {showSubtitle && (
                  <Field className="md:col-span-2" label="副標題">
                    {showMultilineTitles ? (
                      <textarea className={`${inputClass} min-h-[96px] py-3`} value={form.Subtitle || ""} onChange={(event) => updateField("Subtitle", event.target.value)} />
                    ) : (
                      <input className={inputClass} value={form.Subtitle || ""} onChange={(event) => updateField("Subtitle", event.target.value)} />
                    )}
                  </Field>
                )}
                <Field className="md:col-span-2" label="描述文字">
                  <textarea className={`${inputClass} min-h-[110px] py-3`} value={form.Description || ""} onChange={(event) => updateField("Description", event.target.value)} />
                </Field>

                {showImageFields && (
                  <>
                    <ImageField
                      error={errors.ImageUrl}
                      label="桌機圖片"
                      uploading={uploading === "ImageUrl"}
                      value={form.ImageUrl || ""}
                      onChange={(value) => updateField("ImageUrl", value)}
                      onUpload={(event) => handleUpload(event, "ImageUrl")}
                    />
                    <Field className="md:col-span-2" label="圖片替代文字">
                      <input className={inputClass} value={form.ImageAlt || ""} onChange={(event) => updateField("ImageAlt", event.target.value)} />
                    </Field>
                  </>
                )}

                <Field label="主要按鈕文字">
                  <input className={inputClass} value={form.PrimaryButtonLabel || ""} onChange={(event) => updateField("PrimaryButtonLabel", event.target.value)} />
                </Field>
                <Field error={errors.PrimaryButtonUrl} label="主要按鈕連結">
                  <input className={inputClass} placeholder="/booking 或 https://..." value={form.PrimaryButtonUrl || ""} onChange={(event) => updateField("PrimaryButtonUrl", event.target.value)} />
                </Field>
                {showSecondaryButton && (
                  <>
                    <Field label="次要按鈕文字">
                      <input className={inputClass} value={form.SecondaryButtonLabel || ""} onChange={(event) => updateField("SecondaryButtonLabel", event.target.value)} />
                    </Field>
                    <Field error={errors.SecondaryButtonUrl} label="次要按鈕連結">
                      <input className={inputClass} placeholder="/hearing-test 或 https://..." value={form.SecondaryButtonUrl || ""} onChange={(event) => updateField("SecondaryButtonUrl", event.target.value)} />
                    </Field>
                  </>
                )}
                <Field className="md:col-span-2" label="補充提示文字">
                  <input className={inputClass} value={form.NoteText || ""} onChange={(event) => updateField("NoteText", event.target.value)} />
                </Field>
                <Field className="md:col-span-2" label="標籤，一行一個或逗號分隔">
                  <textarea className={`${inputClass} min-h-[96px] py-3`} value={form.TagsText} onChange={(event) => updateField("TagsText", event.target.value)} />
                </Field>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 flex shrink-0 flex-col gap-3 border-t border-[#edf0ee] bg-white/95 px-4 py-3 shadow-[0_-12px_28px_rgba(32,61,50,.08)] backdrop-blur sm:flex-row sm:items-center sm:justify-between">
                <div className="min-h-10 flex-1">
                  {notice && <NoticeMessage compact notice={notice} />}
                </div>
                <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-6 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" disabled={saving || uploading !== null} type="submit">
                  <Save size={18} />
                  {saving ? "儲存中..." : "儲存區塊"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </AdminLayout>
  );
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

function NoticeMessage({ compact = false, notice }: { compact?: boolean; notice: Notice }) {
  const isSuccess = notice.tone === "success";
  const className = isSuccess
    ? "border-[#b8d8c0] bg-[#e9f4ec] text-[#245438]"
    : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]";

  return (
    <div className={`inline-flex w-full items-center gap-2 rounded-md border px-4 py-3 text-sm font-bold ${className} ${compact ? "sm:w-auto" : ""}`}>
      {isSuccess ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
      <span>{notice.message}</span>
    </div>
  );
}

function Field({ children, className = "", error, label }: { children: ReactNode; className?: string; error?: string; label: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-sm font-bold text-[#315746]">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs font-bold text-[#b3261e]">{error}</span>}
    </label>
  );
}

function ImageField({
  error,
  label,
  onChange,
  onUpload,
  uploading,
  value,
}: {
  error?: string;
  label: string;
  onChange: (value: string) => void;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  uploading: boolean;
  value: string;
}) {
  return (
    <Field error={error} label={label}>
      <div className="flex gap-2">
        <input className={inputClass} placeholder="/uploads/..." value={value} onChange={(event) => onChange(event.target.value)} />
        <label className="inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]">
          <ImagePlus size={17} />
          {uploading ? "上傳中" : "上傳"}
          <input accept="image/*" className="hidden" disabled={uploading} onChange={onUpload} type="file" />
        </label>
      </div>
      {value && (
        <div className="mt-2 flex min-h-32 items-center justify-center overflow-hidden rounded-md border border-[#dfe6e1] bg-[#f6f7f5] p-2">
          <img alt="" className="max-h-56 w-full object-contain" src={value} />
        </div>
      )}
    </Field>
  );
}
